import { Suspense, useEffect, useRef, useState } from 'react';
import type {
  EmergencyAccessSessionDetail,
  RecordExternalHandoffConsentPayload,
} from '@usacsmideo/types';
import { toast } from 'sonner';
import { deriveSessionBadges, type SessionBadgeClass } from './sessionBadges';
import { LazySessionLocationMap } from './lazySessionLocationMap';
import { HandoffConsentDialog } from './HandoffConsentDialog';
import type { HandoffIntent } from './handoffConsentCopy';
import { buildDirectionsUrl, buildHandoffUrl, handoffPlatform, isRoutableAccuracy } from './handoff';
import { geolocationAvailable, requestViewerLocation } from './requestViewerLocation';

/**
 * One session card on the Sessions Surface (CU-86aj1c9ev). Presents a timestamp,
 * badges (Timeline Filter Rules taxonomy, CU-86ahjxn9e §D2), and — when the
 * scanner shared their location — a map of that coordinate.
 *
 * INTENTIONAL PHI DISCLOSURE (CU-86ajd3yt0): this surface deliberately discloses
 * the scanner's device-reported coordinate to authorized family/patient viewers.
 * That is an *intended* disclosure, gated server-side by viewer scope
 * (`request.auth.uid`), not an oversight — the audience is emergency family
 * access + the patient's own "My Scans", both appropriate disclosure audiences.
 * The base map is self-hosted (coordinate stays in-boundary); the only
 * third-party crossing is the consent-gated "Open in maps" handoff below.
 */

const BADGE_STYLES: Record<SessionBadgeClass, string> = {
  alert: 'bg-amber-100 text-amber-800 ring-amber-200',
  error: 'bg-red-100 text-red-800 ring-red-200',
  info: 'bg-sky-100 text-sky-800 ring-sky-200',
};

/**
 * CU-86ajd3yt0: the external-handoff consent concern, threaded from the query-
 * owning route through the pure surface down to each card. Bundled as a named
 * type so the intermediate list components (DayGroupedSessions, FamilyGroup)
 * forward it without re-declaring three props apiece.
 */
export interface HandoffConsentProps {
  /**
   * The viewer's PER-VIEWER suppression state (`handoffConsent.dontAskAgain`
   * from getSessionsForViewer). When true, "Open in maps" skips the dialog.
   */
  dontAskAgain?: boolean;
  /** Records the consent ack (the callable), then the card performs the handoff. */
  onRecordHandoffConsent?: (
    _payload: RecordExternalHandoffConsentPayload,
  ) => Promise<unknown>;
  /** True while an ack is being recorded (disables the dialog buttons). */
  isRecordingConsent?: boolean;
}

function formatWhen(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function SessionCard({
  session,
  focused = false,
  now,
  onRefresh,
  isRefreshing = false,
  dontAskAgain = false,
  onRecordHandoffConsent,
  isRecordingConsent = false,
}: {
  session: EmergencyAccessSessionDetail;
  focused?: boolean;
  /** CU-86aj3a0k2: clock for the live-window check (defaults to Date.now()). */
  now?: number;
  /** CU-86aj3a0k2: refetch the sessions query (shown only in the live window). */
  onRefresh?: () => void;
  isRefreshing?: boolean;
} & HandoffConsentProps) {
  const badges = deriveSessionBadges(session);
  const locationShared = (session.events ?? []).some(
    (e) => e.type === 'geo_permission_granted',
  );

  // CU-86ajd3yt0: the scanner's shared coordinate, when present on the wire.
  // When set we render the map; the text note is the fallback for the
  // coords-absent case (a `geo_permission_granted` event without a location).
  const location = session.location;

  const platform = handoffPlatform();

  // Consent-gate state. `intent` selects the copy variant + which handoff runs
  // on confirm ('view' = pin; 'directions' = route).
  const [consentOpen, setConsentOpen] = useState(false);
  const [intent, setIntent] = useState<HandoffIntent>('view');

  // CU-86ajd3yt0: offer "Get directions" only when routing is meaningful —
  // the coordinate is precise enough (accuracy ≤ threshold) AND we can actually
  // route: native maps apps always can (they supply their own origin); on web we
  // need the browser geolocation API to get the viewer's origin for OSM.
  const canRoute =
    !!location &&
    isRoutableAccuracy(location.accuracy) &&
    (platform !== 'web' || geolocationAvailable());

  // Session-cache the viewer's fix so a second "Get directions" tap in the same
  // visit skips even the (permission-free) re-fetch. The browser also remembers
  // the grant itself, so this is belt-and-suspenders. Never persisted off-device.
  const viewerLocationRef = useRef<{ lat: number; lng: number } | null>(null);

  /** Open the coordinate as a pin/area view (no routing). */
  const performViewHandoff = () => {
    if (!location) return;
    window.open(buildHandoffUrl(platform, location), '_blank', 'noopener,noreferrer');
  };

  /**
   * Open directions to the coordinate. Native supplies its own origin; web needs
   * the viewer's location (cached for the session, else requested). On a web
   * geolocation failure we fall back to the plain view + a note — never block.
   */
  const performDirectionsHandoff = async () => {
    if (!location) return;
    if (platform !== 'web') {
      window.open(buildDirectionsUrl(platform, location), '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const origin = viewerLocationRef.current ?? (await requestViewerLocation());
      viewerLocationRef.current = origin;
      window.open(
        buildDirectionsUrl(platform, location, origin),
        '_blank',
        'noopener,noreferrer',
      );
    } catch {
      // Denied / unavailable / timed out — routing needs an origin on web, so
      // fall back to showing the location and tell the viewer why.
      toast.info('Couldn’t get your location — showing the scan location instead.');
      performViewHandoff();
    }
  };

  const performHandoff = (which: HandoffIntent) =>
    which === 'directions' ? performDirectionsHandoff() : performViewHandoff();

  /** CTA tap: hand off directly if already acked, else gate. */
  const handleHandoffTap = (which: HandoffIntent) => {
    if (dontAskAgain) {
      void performHandoff(which);
      return;
    }
    setIntent(which);
    setConsentOpen(true);
  };

  /** Dialog confirm: record the ack, then hand off (proceed even if ack fails). */
  const handleConsentConfirm = async (dontAskAgainChoice: boolean) => {
    try {
      await onRecordHandoffConsent?.({
        sessionId: session.sessionId,
        platform,
        coordShared: true,
        dontAskAgain: dontAskAgainChoice,
      });
    } catch {
      // The ack is audit/awareness, not authorization — the viewer already
      // consented in-UI, so a failed write must not block their own action.
      // (The callable's own Sentry capture covers the failure server-side.)
    }
    setConsentOpen(false);
    void performHandoff(intent);
  };

  // CU-86aj3a0k2: a "Check for updates" button, shown only while the session is
  // still in its live window (now < expiresAt — the server's 15-min teardown
  // clock). After teardown the session can't change, so a refresh would be a
  // dead button; before it, engagement badges (e.g. "Watched 90%") can still
  // arrive. Explicit button (vs. some implicit refresh) for less-technical users.
  const clock = now ?? Date.now();
  const isLive =
    typeof session.expiresAt === 'number' && clock < session.expiresAt;
  const showRefresh = isLive && !!onRefresh;

  // CU-86aj3a0k2: bring the deep-linked card into view. The parent already
  // expands + highlights the focused group, but on a long page (and especially a
  // phone-width portal) the card can sit below the fold — the whole point of a
  // notification deep-link is to SEE that scan. Scroll once focused; guarded for
  // jsdom (no scrollIntoView in tests).
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focused && cardRef.current?.scrollIntoView) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focused]);

  return (
    <div
      ref={cardRef}
      data-session-id={session.sessionId}
      className={`flex w-full flex-col gap-2 rounded-md border bg-white p-4 text-left shadow-sm ${
        focused ? 'border-sky-500 ring-2 ring-sky-300' : 'border-gray-200'
      }`}
    >
      <p className="text-sm font-medium text-gray-700">
        {formatWhen(session.createdAt)}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {badges.map((b, i) => (
          <span
            key={`${b.label}-${i}`}
            className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${BADGE_STYLES[b.badgeClass]}`}
          >
            {b.label}
          </span>
        ))}
      </div>
      {location ? (
        <div className="flex flex-col gap-2">
          {/* Lazy map (CU-86ajd3yt0): maplibre is split to its own chunk and
              prefetched on /sessions mount. Fallback is a quiet styled
              placeholder (NOT a spinner) matching the map's own container — on
              this emergency surface the caption + "Open in maps" below render
              instantly regardless, and the chunk is usually already cached. */}
          <Suspense
            fallback={
              <div
                data-testid="session-location-map-loading"
                aria-hidden="true"
                className="h-48 w-full rounded-md border border-gray-200 bg-gray-100"
              />
            }
          >
            <LazySessionLocationMap location={location} />
          </Suspense>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleHandoffTap('view')}
              className="rounded-md border border-sky-600 px-3 py-1.5 text-sm font-semibold text-sky-700 hover:bg-sky-50"
            >
              View in larger map
            </button>
            {/* CU-86ajd3yt0: directions only when routing is meaningful — precise
                enough to route AND (native, or web with geolocation available). */}
            {canRoute && (
              <button
                type="button"
                onClick={() => handleHandoffTap('directions')}
                className="rounded-md border border-sky-600 px-3 py-1.5 text-sm font-semibold text-sky-700 hover:bg-sky-50"
              >
                Get directions
              </button>
            )}
          </div>
          <HandoffConsentDialog
            isOpen={consentOpen}
            platform={platform}
            intent={intent}
            onConfirm={handleConsentConfirm}
            onCancel={() => setConsentOpen(false)}
            isRecording={isRecordingConsent}
          />
        </div>
      ) : locationShared ? (
        // Location was shared (geo_permission_granted) but no coordinate reached
        // the wire — the fieldless legacy/edge case. Keep the shared-but-no-coord note.
        <p className="text-xs text-gray-500">📍 Location was shared during this access</p>
      ) : (
        // CU-86ajd3yt0: no location signal at all. State it affirmatively so a
        // family viewer knows the absence is real ("was a location captured? no"),
        // not a load failure.
        <p className="text-xs text-gray-400">📍 Location not shared at scan time</p>
      )}
      {showRefresh && (
        <button
          type="button"
          aria-label="Check this scan for updates"
          aria-busy={isRefreshing}
          disabled={isRefreshing}
          onClick={onRefresh}
          className="mt-1 self-start rounded-md border border-sky-600 px-3 py-1.5 text-sm font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-60"
        >
          {isRefreshing ? 'Checking…' : '↻ Check for updates'}
        </button>
      )}
    </div>
  );
}

export default SessionCard;
